import { useNavigate } from "react-router-dom";
import { RRHH2Form } from "../../../components/FormularioRRHH2";

import { useEffect, useState, useMemo } from "react";
import { Box, Container, Tab, Tabs, Button, Grid, Alert } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import WorkIcon from "@mui/icons-material/Work";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import AccountBoxIcon from "@mui/icons-material/AccountBox";

import { useLegajosContext } from "../../../context/LegajoTabsContext";
import PersonalForm from "../components/form-tabs/PersonalForm";
import FamiliaresForm from "../components/form-tabs/FamiliaresForm";
import FotoForm from "../components/form-tabs/FotoForm";
import LaboralesForm from "../components/form-tabs/LaboralesForm";
import { createEmpleado, updateEmpleado } from "../services/empleados.api";
import { useToast } from "../../../../../Hooks/UseToast";
import { validateForm } from "../utils/validationEmpleado";
import InventarioForm from "../components/form-tabs/InventarioForm";
import { useEmpleados } from "../hooks/useEmpleados";
import { Edit } from "@mui/icons-material";
import { prepareLegajoForSave, hasTabErrors } from "../utils/legajoAdapter";
import { logEventos } from "../../../../../services/services";
import { coloresEmpresas } from "../utils/coloresEmpresas";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 6 }}>{children}</Box>}
    </div>
  );
}

// Static configuration for tabs
const TABS_CONFIG = [
  {
    value: "personal",
    label: "Datos Personales",
    icon: <PersonIcon sx={{ fontSize: 20, mr: 1 }} />,
    description: "Información personal",
  },
  {
    value: "laboral",
    label: "Datos Laborales",
    icon: <WorkIcon sx={{ fontSize: 20, mr: 1 }} />,
    description: "Información laboral",
  },
  {
    value: "familiares",
    label: "Familiares",
    icon: <FamilyRestroomIcon sx={{ fontSize: 20, mr: 1 }} />,
    description: "Información de familia",
  },
  {
    value: "inventario",
    label: "Inventario",
    icon: <CheckroomIcon sx={{ fontSize: 20, mr: 1 }} />,
    description: "Información de inventario",
  },
  {
    value: "foto",
    label: "Foto de Empleado",
    icon: <AccountBoxIcon sx={{ fontSize: 20, mr: 1 }} />,
    description: "Foto y credencial",
  },
];

export default function LegajosFormPage({ cuil }) {
  const navigate = useNavigate();
  const {
    formData,
    updateFormData,
    ensureUuidFoto,
    errors,
    setErrors,
    setArrayData,
    resetForm, // Import resetForm
  } = useLegajosContext();
  const { getEmpleadoByCUIL } = useEmpleados();
  const { showToast, Toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const { referenceData } = useLegajosContext();
  const { sucursales } = referenceData;

  const colorEmpresa = useMemo(() => {
    const sucursalId = formData?.datosLaborales?.sucursal_id;
    if (!sucursalId || !sucursales) return "#1F618D";

    const sucursal = sucursales.find((s) => s.id === sucursalId);
    if (!sucursal) return "#1F618D";
    const empresaId = sucursal.empresa;
    return coloresEmpresas[empresaId] || "#1F618D";
  }, [formData?.datosLaborales?.sucursal_id, sucursales]);

  const user = localStorage.getItem("user");

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (cuil) {
      const fetchEmpleado = async () => {
        try {
          const empleado = await getEmpleadoByCUIL(cuil);

          if (empleado && !empleado.error) {
            updateFormData("datosPersonales", {
              ...empleado.datosPersonales,
            });
            updateFormData("datosLaborales", { ...empleado.datosLaborales });
            setArrayData(empleado.familiares || [], "familiares");
            setArrayData(empleado.telefonosEmpleado || [], "telefonosEmpleado");
            setIsEditing(true);
          } else {
            showToast(
              empleado?.error || "No se pudo cargar el legajo del empleado",
              "error",
            );
          }
        } catch (error) {
          console.error("Error fetching empleado:", error);
        }
      };

      fetchEmpleado();
    }
  }, [cuil, getEmpleadoByCUIL, updateFormData, setArrayData]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const uuid = ensureUuidFoto();

    // Validate
    if (!validateForm(formData, setErrors)) {
      showToast("Por favor, corrija los errores en el formulario", "info");
      return;
    }

    // Adapt data for API
    const dataToSend = prepareLegajoForSave(formData, uuid);

    try {
      let result;
      if (cuil) {
        result = await updateEmpleado(cuil, dataToSend);
        
        logEventos(
          "Empleado actualizado",
          "Legajos",
          "actualizar empleado",
          user,
        );

      } else {
        result = await createEmpleado(dataToSend);
        logEventos("Empleado creado", "Legajos", "crear empleado", user);
      }
      if (result.message) {
        logEventos("Empleado guardado", "Legajos", "guardar empleado", user);
        showToast(result.message, "success");
        setTimeout(() => {
          resetForm(); // Clear context on success
          if (cuil) {
            navigate("/rrhh2/legajos");
          } else {
            navigate("/rrhh2/legajos/creado", {
              state: { DataEmpleado: dataToSend },
            });
          }
        }, 1000);
      } else if (result.error) {
        showToast(result.error, "error");
      }
    } catch (error) {
      showToast("Error al guardar el legajo", "error");
      logEventos(
        "Error al guardar el legajo",
        "Legajos",
        "guardar legajo",
        user,
      );
    }
  };

  const handleClose = () => {
    resetForm(); // Clear context on close
    navigate("/rrhh2/legajos");
  };

  return (
    <RRHH2Form
      title="Legajos del Personal"
      onSubmit={handleSave}
      onClose={handleClose}
      disabled={isEditing}
      colorEmpresa={colorEmpresa}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="legajos tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: "rgba(0, 0, 0, 0.06)",
          bgcolor: "#fafbfc",
          "& .MuiTabs-indicator": {
            height: 3,
            bgcolor: "#1565c0",
            borderRadius: "3px 3px 0 0",
          },
          "& .MuiTab-root": {
            fontWeight: 600,
            fontSize: "0.95rem",
            textTransform: "none",
            color: "#64748b",
            letterSpacing: "0.3px",
            py: 2.5,
            px: 3,
            mx: 1,
            minWidth: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            "&:hover": {
              color: "#1565c0",
              bgcolor: "rgba(21, 101, 192, 0.04)",
            },
            "&.Mui-selected": {
              color: "#1565c0",
              fontWeight: 700,
            },
          },
        }}
      >
        {TABS_CONFIG.map((tab, index) => (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={tab.icon}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {tab.label}
                {hasTabErrors(tab.value, errors) && (
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      bgcolor: "error.main",
                      borderRadius: "50%",
                      ml: 1,
                      boxShadow: "0 0 0 2px #ee4545ff",
                    }}
                  />
                )}
              </Box>
            }
            id={`tab-${index}`}
            aria-controls={`tabpanel-${index}`}
          />
        ))}
      </Tabs>
      <Box sx={{ px: { xs: 2, md: 5, lg: 4 } }}>
        <TabPanel value={activeTab} index={"personal"}>
          <PersonalForm isEditing={isEditing} cuil={cuil} />
        </TabPanel>

        <TabPanel value={activeTab} index={"laboral"}>
          <LaboralesForm isEditing={isEditing} />
        </TabPanel>

        <TabPanel value={activeTab} index={"familiares"}>
          <FamiliaresForm isEditing={isEditing} cuil={cuil} />
        </TabPanel>
        <TabPanel value={activeTab} index={"inventario"}>
          <InventarioForm isEditing={isEditing} />
        </TabPanel>
        <TabPanel value={activeTab} index={"foto"}>
          <FotoForm isEditing={isEditing} cuil={cuil} />
        </TabPanel>
      </Box>
      {isEditing ? (
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Button
            onClick={() => setIsEditing((prevData) => !prevData)}
            variant="contained"
            color="warning"
            startIcon={<Edit />}
          >
            Editar Empleado
          </Button>
        </Grid>
      ) : null}
      {Toast}
      {Object?.keys(errors)?.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="error">
            Por favor, corrija los errores en el formulario
          </Alert>
        </Grid>
      )}
    </RRHH2Form>
  );
}
